import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import SignaturePad from "../components/SignaturePad";

const API = window.__RUNTIME_CONFIG__?.apiBase || window.location.origin;

const DOC_LABELS = {
  offer_letter: "Offer Letter",
  contract: "Employment Contract",
  contract_employer: "Employment Contract",
  criminal_check: "Criminal Check Consent",
  pledge_confidentiality: "Pledge of Confidentiality",
  cellphone_policy: "Company Cell Phone Policy",
  contract_addendum: "Contract Addendum",
  contract_addendum_employer: "Contract Addendum",
};

const EMPLOYER_COUNTERSIGN_DOC_TYPES = ["contract_addendum_employer", "contract_employer"];

export default function SignDocument() {
  const { docType, token } = useParams();
  const docLabel = DOC_LABELS[docType] || "Document";
  const isEmployerCountersign = EMPLOYER_COUNTERSIGN_DOC_TYPES.includes(docType);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [agree, setAgree] = useState(false);

  // criminal_check-only mini-form — candidate must answer before signing
  const [previousConvictions, setPreviousConvictions] = useState(null); // null | true | false
  const [convictionDetails, setConvictionDetails] = useState("");

  const sigRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/sign/${docType}/${token}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setError(d.error);
        else setInfo(d);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not connect to the server. Please try again later.");
        setLoading(false);
      });
  }, [docType, token]);

  const criminalCheckMiniFormValid = () => {
    if (docType !== "criminal_check") return true;
    if (previousConvictions === null) return false;
    if (previousConvictions === true && !convictionDetails.trim()) return false;
    return true;
  };

  const canSubmit = () => {
    if (!agree) return false;
    if (!sigRef.current?.canSubmit()) return false;
    if (!criminalCheckMiniFormValid()) return false;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const method = sigRef.current.getMethod();
      const signature_data = sigRef.current.getSignatureData();

      const body = { method, signature_data };
      if (docType === "criminal_check") {
        body.previous_convictions = !!previousConvictions;
        if (previousConvictions) body.conviction_details = convictionDetails.trim();
      }

      const res = await fetch(`${API}/api/sign/${docType}/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not submit signature");
      setSubmitted(true);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    background: "#f0f4f8",
    padding: "30px 16px",
    fontFamily: "system-ui, sans-serif",
    display: "flex",
    justifyContent: "center",
  };
  const cardStyle = {
    background: "white",
    borderRadius: 16,
    padding: 32,
    maxWidth: 720,
    width: "100%",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
    height: "fit-content",
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}><p style={{ textAlign: "center", color: "#6b7280" }}>Loading…</p></div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48 }}>❌</div>
            <h2 style={{ color: "#991b1b" }}>Link Unavailable</h2>
            <p style={{ color: "#6b7280" }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted || info?.already_signed) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <h2 style={{ color: "#166534" }}>{submitted ? "Signed Successfully!" : "Already Signed"}</h2>
            <p style={{ color: "#6b7280" }}>
              {submitted
                ? (isEmployerCountersign
                    ? `Thank you — the fully executed ${docLabel.toLowerCase()} has been emailed to the candidate.`
                    : "Thank you — a copy of the signed document has been emailed to you.")
                : `This document was already signed${info?.signed_at ? " on " + new Date(info.signed_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : ""}.`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={{ marginTop: 0 }}>{docLabel}</h1>
        <p style={{ color: "#6b7280" }}>
          {isEmployerCountersign
            ? `${info?.candidate_name} has signed this ${docLabel}. Please review and countersign it below to finalize it.`
            : `Hi ${info?.candidate_name}, please review your ${docLabel.toLowerCase()} below and sign to confirm your acceptance.`}
        </p>

        <div style={{ margin: "16px 0" }}>
          <a
            href={`${API}/api/sign/${docType}/${token}/preview`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block", padding: "10px 20px", borderRadius: 8,
              background: "#2980b9", color: "white", textDecoration: "none", fontWeight: 600,
            }}
          >
            📄 Open Document to Read
          </a>
        </div>

        {docType === "criminal_check" && info && (
          <div style={{ margin: "16px 0", padding: 16, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
            <h3 style={{ marginTop: 0, marginBottom: 10 }}>Your Details</h3>
            <div style={{ fontSize: 14, color: "#374151", display: "grid", gap: 4, marginBottom: 16 }}>
              <div><strong>Name:</strong> {info.full_name}</div>
              <div><strong>Surname:</strong> {info.surname}</div>
              <div><strong>ID / Passport Number:</strong> {info.id_number || "—"}</div>
              <div><strong>Date of Birth:</strong> {info.date_of_birth || "—"}</div>
              <div><strong>Mobile Number:</strong> {info.mobile_number || "—"}</div>
              <div><strong>Email:</strong> {info.candidate_email || "—"}</div>
              <div><strong>Address:</strong> {info.address || "—"}</div>
              {info.company_name && <div><strong>Company:</strong> {info.company_name}</div>}
            </div>

            <h3 style={{ marginBottom: 8 }}>Checks to be Performed</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {(info.checks_selected || []).map((c) => (
                <span key={c} style={{ padding: "5px 12px", borderRadius: 20, background: "#ede9fe", color: "#5b21b6", fontSize: 13, fontWeight: 600 }}>
                  {c}
                </span>
              ))}
            </div>

            <h3 style={{ marginBottom: 8 }}>Previous Convictions?</h3>
            <div style={{ display: "flex", gap: 20, marginBottom: previousConvictions ? 12 : 0 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 14 }}>
                <input type="radio" name="prevConvictions" checked={previousConvictions === false} onChange={() => setPreviousConvictions(false)} /> No
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 14 }}>
                <input type="radio" name="prevConvictions" checked={previousConvictions === true} onChange={() => setPreviousConvictions(true)} /> Yes
              </label>
            </div>
            {previousConvictions === true && (
              <div>
                <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 6 }}>
                  Please provide details (date, offence, sentence) *
                </label>
                <textarea
                  required
                  value={convictionDetails}
                  onChange={(e) => setConvictionDetails(e.target.value)}
                  rows={3}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, boxSizing: "border-box", fontFamily: "inherit" }}
                />
              </div>
            )}
          </div>
        )}

        <SignaturePad ref={sigRef} />

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 20, fontSize: 14, color: "#374151", cursor: "pointer" }}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
          <span>I confirm this is my signature and I agree to the terms of this {docLabel.toLowerCase()}.</span>
        </label>

        {error && <p style={{ color: "#991b1b", fontSize: 14, marginTop: 10 }}>{error}</p>}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit() || submitting}
          style={{
            marginTop: 16, width: "100%", padding: "14px 20px", borderRadius: 8, border: "none",
            background: canSubmit() && !submitting ? "#166534" : "#9ca3af",
            color: "white", fontWeight: 700, fontSize: 16,
            cursor: canSubmit() && !submitting ? "pointer" : "default",
          }}
        >
          {submitting ? "Submitting…" : "Submit Signature"}
        </button>
      </div>
    </div>
  );
}
